import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

function ConfirmDialog({ confirmState, onConfirm, onCancel }) {
  const isOpen = Boolean(confirmState?.open);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
            onClick={onCancel}
          />

          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.24)] dark:border-slate-700 dark:bg-[#14212d]"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <div className={`absolute inset-x-0 top-0 h-1.5 ${
              confirmState?.variation === 'danger'
                ? 'bg-rose-500'
                : confirmState?.variation === 'warning'
                  ? 'bg-amber-500'
                  : 'bg-sky-500'
            }`} />

            <div className="p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <div className={`mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                  confirmState?.variation === 'danger'
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                    : confirmState?.variation === 'warning'
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                }`}>
                  <span className="material-icons-round text-[24px]">
                    {confirmState?.icon || 'help'}
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${
                      confirmState?.variation === 'danger'
                        ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                        : confirmState?.variation === 'warning'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                    }`}>
                      {confirmState?.badge || 'Confirmación'}
                    </span>
                  </div>

                  <h3
                    id="confirm-dialog-title"
                    className="mt-3 text-lg font-bold text-slate-900 dark:text-white"
                  >
                    {confirmState?.title || '¿Deseas continuar?'}
                  </h3>

                  <p
                    id="confirm-dialog-description"
                    className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300"
                  >
                    {confirmState?.message || 'Confirma esta acción para continuar.'}
                  </p>

                  {confirmState?.details && (
                    <div className="mt-4 max-h-[45vh] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
                      {confirmState.details}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {confirmState?.cancelText || 'Cancelar'}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className={`inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors ${
                    confirmState?.variation === 'danger'
                      ? 'bg-rose-600 hover:bg-rose-700'
                      : confirmState?.variation === 'warning'
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-[#2D5BA6] hover:bg-[#244d8f]'
                  }`}
                >
                  {confirmState?.confirmText || 'Confirmar'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default ConfirmDialog;
